import React, { useCallback, useContext, useMemo, useState } from 'react';

import { Skeleton } from '@mui/material';
import { useStatsigClient } from '@statsig/react-bindings';
import { useTranslation } from 'react-i18next';
import { useIntercom } from 'react-use-intercom';

import BdsAvatar from '../../../../shared/components/v3/BdsAvatar/BdsAvatar';
import BdsLabelPill from '../../../../shared/components/v3/BdsLabelPill/BdsLabelPill';
import BdsTooltip from '../../../../shared/components/v3/BdsTooltip/BdsTooltip';
import BdsButton from '../../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import BdsIconButton from '../../../../shared/components/v3/Buttons/BdsIconButton/BdsIconButton';
import GenericMenu from '../../../../shared/components/v3/GenericMenu/GenericMenu';
import BdsModal from '../../../../shared/components/v3/Modals/BdsModal/BdsModal';
import Toast from '../../../../shared/components/v3/Toast/Toast';
import { COLLABORATOR_STATUS, COLLABORATORS_DETAILS_STATUS } from '../../../../shared/constants/collaborators';
import { ARROW_LEFT_ICON, CHEVRON_DOWN_V3, DOWNLOAD_SIMPLE } from '../../../../shared/constants/images';
import { CERTIFICATE_NOT_FOUND_MODAL, DEFAULT_BDS_MODAL } from '../../../../shared/constants/modal';
import { PLAN_TYPE_HEALTH } from '../../../../shared/constants/plan';
import { CAN_DOWNLOAD_NOVATION_FG } from '../../../../shared/constants/statsig';
import {
  CLICK_ACTION,
  DOWNLOAD_CERTIFICATE,
  DOWNLOAD_NOVATION,
  SECTION_COLLABORATOR_DETAIL,
  SECTION_COLLABORATOR_TABLE
} from '../../../../shared/constants/tagManager';
import { GENERIC_ERROR_TOAST } from '../../../../shared/constants/toast';
import { CompanyContext } from '../../../../shared/context/company/companyContext';
import { PlansContext } from '../../../../shared/context/plans/plansContext';
import { useScrollState } from '../../../../shared/hooks/useScrollState/useScrollState';
import { IBdsModalContent } from '../../../../shared/interfaces/bdsModal';
import { ICollaboratorDetailHero, IInsuranceCertificate } from '../../../../shared/interfaces/collaborators';
import { IToast } from '../../../../shared/interfaces/toast';
import Companies from '../../../../shared/services/api/collaborators/collaborators';
import TagManagerService from '../../../../shared/services/gtm/tagManagerService';
import {
  CERTIFICATE_TYPE_TRANSLATIONS,
  TRANSLATION_COLLABORATORS,
  TRANSLATIONS_COLLABORATOR_STATUS_V3
} from '../../../../shared/translations/collaborators';
import { formatDateForDisplay } from '../../../../shared/utils/datetime/datetime';
import { downloadFile } from '../../../../shared/utils/utils';

import './CollaboratorDetailsHero.scss';

const CollaboratorsDetailHero = ({
  collaborator,
  planName,
  backAction,
  hasNovation,
  isFR,
  loading
}: ICollaboratorDetailHero) => {
  const { companyData, isSpain, isMexico, isUSA } = useContext(CompanyContext);
  const { plans } = useContext(PlansContext);
  const isScrolled = useScrollState(0);
  const { client } = useStatsigClient();
  const [isLoadingNovation, setIsLoadingNovation] = useState(false);
  const [isLoadingCertificates, setIsLoadingCertificates] = useState(false);
  const [certificates, setCertificates] = useState<IInsuranceCertificate[]>([]);
  const [showCertificateMenu, setShowCertificateMenu] = useState(false);
  const [toastInfo, setToastInfo] = useState<{ open: boolean; data: IToast }>({
    open: false,
    data: GENERIC_ERROR_TOAST
  });
  const notActiveCollaborator = collaborator.status !== COLLABORATOR_STATUS.active;
  const isNovationButtonDisabled = isLoadingNovation || !hasNovation || notActiveCollaborator;
  const shouldShowTooltip = !hasNovation || notActiveCollaborator;
  const canDownloadNovation = client.checkGate(CAN_DOWNLOAD_NOVATION_FG);
  const showNovationButton = canDownloadNovation && isFR && isSpain;

  const collaboratorName = useMemo(() => {
    return collaborator.fullName || collaborator.email;
  }, [collaborator.fullName, collaborator.email]);

  const plan = collaborator.plans[0];
  const currentPlan = plans?.find((p) => p.id === Number(plan));
  const isHealthPlan = currentPlan?.type === PLAN_TYPE_HEALTH;
  const showCertificateButton = isMexico && isHealthPlan;
  const [certNotFoundModal, setCertNotFoundModal] = useState(false);
  const [modalContent, setModalContent] = useState<IBdsModalContent>(DEFAULT_BDS_MODAL);
  const { show } = useIntercom();
  const displayPendingTooltip = collaborator.status === 'pending' && collaborator.planStartDate;
  const { t } = useTranslation();

  const openIntercom = () => {
    show();
    setCertNotFoundModal(false);
  };

  const renderSkeleton = (width: number) => {
    return <Skeleton animation={false} className="skeleton" variant="rectangular" width={width} height={24} />;
  };

  const downloadNovation = useCallback(async () => {
    setIsLoadingNovation(true);
    try {
      const response = await Companies.downloadNovationAgreement(companyData.id, collaborator.email);
      TagManagerService.pushEvent(CLICK_ACTION, DOWNLOAD_NOVATION, SECTION_COLLABORATOR_TABLE);
      downloadFile(response.url, true);
    } catch {
      setToastInfo({ open: true, data: GENERIC_ERROR_TOAST });
    } finally {
      setIsLoadingNovation(false);
    }
  }, [companyData.id, collaborator.email]);

  const fetchCertificates = useCallback(async () => {
    setIsLoadingCertificates(true);
    try {
      const response = await Companies.downloadInsuranceCertificates(companyData.id, collaborator.email);
      if (!response || response.length === 0) {
        setModalContent(CERTIFICATE_NOT_FOUND_MODAL);
        setCertNotFoundModal(true);
        return;
      }
      setCertificates(response);
      setShowCertificateMenu(true);
    } catch {
      setToastInfo({ open: true, data: GENERIC_ERROR_TOAST });
    } finally {
      setIsLoadingCertificates(false);
    }
  }, [companyData.id, collaborator.email]);

  const handleCertificateDownload = useCallback((certificate: IInsuranceCertificate) => {
    TagManagerService.pushEvent(CLICK_ACTION, DOWNLOAD_CERTIFICATE, SECTION_COLLABORATOR_DETAIL);
    downloadFile(certificate.url, true);
  }, []);

  const handleCloseToast = useCallback(() => {
    setToastInfo((prev) => ({ ...prev, open: false }));
  }, []);

  const renderStatus = () => {
    const { status, joinedAt, lastInvitationSentAt } = collaborator;
    const translationKey =
      status === 'active' ? TRANSLATION_COLLABORATORS.detail.joinedAt : TRANSLATION_COLLABORATORS.detail.invitedAt;

    const date = status === 'active' ? joinedAt : lastInvitationSentAt;

    if (!date) {
      return null;
    }

    const formattedDate = formatDateForDisplay(date, isUSA, '-');

    return (
      <>
        <div className="separator">{'•'}</div>
        <span className="date">{t(translationKey, { date: formattedDate })}</span>
      </>
    );
  };

  const novationButton = useMemo(() => {
    return (
      <BdsButton
        action={downloadNovation}
        variant="tertiary"
        size="m"
        iconLeft={DOWNLOAD_SIMPLE}
        loading={isLoadingNovation}
        disabled={isNovationButtonDisabled}
      >
        {t(TRANSLATION_COLLABORATORS.detail.novation.downloadButton)}
      </BdsButton>
    );
  }, [downloadNovation, isLoadingNovation, isNovationButtonDisabled]);

  const certificateDropdownData = useMemo(() => {
    return {
      sections: [
        {
          columns: certificates.map((cert) => ({
            label: t(CERTIFICATE_TYPE_TRANSLATIONS[cert.type] || cert.type),
            icon: { component: DOWNLOAD_SIMPLE },
            action: () => handleCertificateDownload(cert)
          }))
        }
      ]
    };
  }, [certificates, t, handleCertificateDownload]);

  const certificateButton = useMemo(() => {
    return (
      <div className="certificate-dropdown-container">
        <BdsButton
          action={fetchCertificates}
          variant="tertiary"
          size="m"
          iconLeft={CHEVRON_DOWN_V3}
          loading={isLoadingCertificates}
        >
          {t(TRANSLATION_COLLABORATORS.detail.certificate.downloadButton)}
        </BdsButton>
        <GenericMenu
          type="dropdown"
          title=""
          showGenericMenu={showCertificateMenu}
          setShowGenericMenu={setShowCertificateMenu}
          dropdownData={certificateDropdownData}
          className="certificate-menu"
        />
      </div>
    );
  }, [fetchCertificates, isLoadingCertificates, showCertificateMenu, certificateDropdownData, t]);

  const displayLabelPill = () => {
    return (
      <BdsLabelPill
        text={t(TRANSLATIONS_COLLABORATOR_STATUS_V3[collaborator.status]).toUpperCase()}
        variant={COLLABORATORS_DETAILS_STATUS[collaborator.status].tagVariant}
        iconLeft={COLLABORATORS_DETAILS_STATUS[collaborator.status].icon}
      />
    );
  };

  return (
    <div
      className={`collaborator-details-hero flex flex-col justify-start items-start ${isScrolled ? 'shrink' : ''} ${
        loading ? 'loading' : ''
      } w-full`}
    >
      <div className="icon-back">
        <BdsIconButton action={backAction} size="sm" icon={ARROW_LEFT_ICON} variant="transparent" />
      </div>
      <div className="info flex flex-row justify-start items-center w-full">
        <BdsAvatar
          className="avatar"
          size="l"
          src={{ initials: collaboratorName, photoUri: collaborator.photo }}
          showRing={!loading}
          loading={loading}
        />
        <div className={`bottom flex flex-col w-full ${isScrolled ? 'shrink' : ''} ${loading ? 'loading' : ''}`}>
          {loading ? (
            <div className="skeletons flex flex-col">
              {renderSkeleton(327)}
              <div className="flex flex-row justify-between">
                {renderSkeleton(327)}
                {showNovationButton && renderSkeleton(200)}
              </div>
            </div>
          ) : (
            <>
              <h2 className="name">{collaboratorName}</h2>
              <div className="bottom-information flex justify-between items-center">
                {!isScrolled && (
                  <div className="flex flex-row gap-3">
                    {displayPendingTooltip ? (
                      <BdsTooltip
                        message={t(TRANSLATION_COLLABORATORS.table.tooltips.pendingCollaborator, {
                          activation_date: formatDateForDisplay(collaborator.planStartDate || '', isUSA, '-')
                        })}
                        position="bottom-left"
                      >
                        {displayLabelPill()}
                      </BdsTooltip>
                    ) : (
                      displayLabelPill()
                    )}
                    <div className="labels flex flex-row justify-start items-center">
                      <div className="plan">{planName}</div>
                      {renderStatus()}
                    </div>
                  </div>
                )}
                {showNovationButton && (
                  <div className="novation-agreement">
                    {shouldShowTooltip ? (
                      <BdsTooltip
                        message={t(TRANSLATION_COLLABORATORS.detail.novation.tooltipMessage)}
                        position="left"
                        variant="standard"
                      >
                        {novationButton}
                      </BdsTooltip>
                    ) : (
                      novationButton
                    )}
                  </div>
                )}
                {showCertificateButton && certificateButton}
              </div>
            </>
          )}
        </div>
      </div>
      <BdsModal
        open={certNotFoundModal}
        title={t(modalContent.title as string)}
        description={t(modalContent.description as string)}
        icon={modalContent.icon}
        onClose={() => setCertNotFoundModal(false)}
        primaryAction={{
          label: t(modalContent.primaryActionText as string),
          onClick: openIntercom
        }}
        secondaryAction={{
          label: t(modalContent.secondaryActionText as string),
          onClick: () => setCertNotFoundModal(false)
        }}
        reverseButtons={true}
      />
      <Toast
        title={t(toastInfo.data.title)}
        description={t(toastInfo.data.description)}
        icon={toastInfo.data.icon}
        open={toastInfo.open}
        button={toastInfo.data.button}
        buttonAction={handleCloseToast}
        handleClose={handleCloseToast}
      />
    </div>
  );
};

export default CollaboratorsDetailHero;
